import { useAppDispatch, useAppSelector } from "app/hooks";
import Button from "shared/components/Button";
import Panel from "shared/components/Panel";
import DataItem from "shared/components/DataItem";
import StatIcon from "shared/components/StatIcon";
import { defineFieldKPIs } from "shared/components/StatIcon/statIconModels";
import { setOpenPanel } from "features/settings/details-slice";
import {
  fieldReportNewWindow,
  scoutDateOnClick,
  selectFieldOnClick,
  sphereDateOnClick,
} from "shared/helpers/sharedOnClickFunctions";
import { useCallback } from "react";
import { ReactComponent as SettingsSVG } from "assets/icons/settings.svg";
import { normaliseValue } from "shared/helpers/unitHelpers";
import { notNullNorUndefined } from "shared/helpers/notNullNorUndefined";

const KPIs = defineFieldKPIs([
  // Overall good health
  "health",
  "cropcover",
  "crop_uniformity",
  // Good health (other)
  "gai",
  "count",
  "count_per_area",
  "ear_count",
  "ear_count_per_area",
  "growth_stage_aab",
  "growth_stage_bbch",
  // Overual bad health
  "unhealthy",
  "weeds",
  "insect_damage",
  // Bad health (other)
  "flowering",
  "ripening",
  "desaturated",
  "yellow_leaves",
  "purple_leaves",
  //
] as const);

interface PanelFarmDetailsProps {}
const PanelFarmDetails: React.FC<PanelFarmDetailsProps> = () => {
  const guid = useAppSelector((state) => state.panorama.guid);
  const farmName = useAppSelector(
    (state) => state.panorama.farmDetails?.farmname
  );
  const currentFieldDetails = useAppSelector(
    (state) => state.panorama.currentFieldDetails
  );
  const imperialUnits = useAppSelector((state) => state.panorama.imperialUnits);

  const units: Parameters<typeof normaliseValue>[2] = imperialUnits
    ? "imperial"
    : "metric";

  const showPanel = useAppSelector(
    (state) => state.settings.openPanel === "farmDetails"
  );
  const dispatch = useAppDispatch();
  const closePanel = useCallback(
    () => dispatch(setOpenPanel(null)),
    [dispatch]
  );

  return (
    <Panel
      showPanel={showPanel}
      direction="left"
      retainBottomMenu
      headerBorder="BorderBottom"
      closePanel={closePanel}
      includeBaseHeader
      underlayClass="NoUnderlay"
      headerElement={
        <div className="DisplayFlex SpaceBetween AlignCentre">
          <div className="DisplayFlex FlexColumn VSmallGap">
            {/* Farm & Field (select field) */}
            <h3>{farmName}</h3>
            <h1>{currentFieldDetails?.title}</h1>
          </div>
          <div>
            <Button
              colour="ThemeAlt"
              additionalClassNames="BtnLink"
              // TEMP: Phase 2 - Field Selector
              disabled
              onClick={selectFieldOnClick(dispatch)}
              children="Select Field"
            />
          </div>
        </div>
      }
      contentClass="FlexContent"
    >
      {notNullNorUndefined(currentFieldDetails) ? (
        <div className="PanelFarmDetails DisplayFlex FlexColumn">
          {/* Field info */}
          <div className="DisplayFlex FlexColumn">
            <h3>Field Info</h3>
            {/* Field size */}
            <DataItem
              left="Size"
              middle={`${normaliseValue(
                parseFloat(currentFieldDetails.area),
                "fieldArea",
                units,
                " [unit]"
              )}`}
            />
            {/* Crop type */}
            <DataItem
              left="Crop type"
              middle={currentFieldDetails.crop_type || "No crop"}
            />
            {/* Weeks since planted */}
            <DataItem
              left="Week"
              middle={currentFieldDetails.weeks_after_plant || 0}
            />
            {/* Plant Date */}
            <DataItem
              left="Plant date"
              middle={currentFieldDetails.plant_date || "No date"}
            />
            {/* Sphere date */}
            <DataItem
              left="Sphere date"
              middle={currentFieldDetails.panodate || "No date"}
              right={
                <Button
                  colour="ThemeAlt"
                  additionalClassNames="BtnLink"
                  // TEMP: Phase 2 - Sphere selector
                  disabled
                  onClick={sphereDateOnClick(dispatch)}
                  children="Select"
                />
              }
            />
            {/* Scout date */}
            <DataItem
              left="Scout date"
              middle={currentFieldDetails.shortdate}
              right={
                <Button
                  colour="ThemeAlt"
                  additionalClassNames="BtnLink"
                  // TEMP: Phase 2 - Sphere selector
                  disabled
                  onClick={scoutDateOnClick(dispatch)}
                  children="Select"
                />
              }
            />
          </div>
          {/* Spacer */}
          <hr />
          {/* Measurements */}
          <div className="MeasurementsParent DisplayFlex FlexColumn">
            <div className="DisplayFlex SpaceBetween AlignCentre">
              <h3>Measurements</h3>
              <Button
                colour="ThemeAlt"
                additionalClassNames="BtnLink"
                disabled={!currentFieldDetails.field_report_url || !guid}
                onClick={fieldReportNewWindow(currentFieldDetails, guid)}
                // onClick={fieldReportInRightPanel(dispatch)}
                children="View Report"
              />
            </div>
            <div className="DisplayFlex FlexColumn">
              {KPIs.map((kpi, index) => (
                <StatIcon
                  key={`KPI_${index}`}
                  targetObject="field"
                  targetKey={kpi}
                  // NOTE: As `any` is required here until we fix the
                  // `unknown` types in the API
                  value={currentFieldDetails[kpi] as any}
                  imperialUnits={imperialUnits}
                  iconSize={24}
                  parentClassNames="DisplayFlex AlignCentre SmallGap FitWidth"
                  viewType="dataItem"
                  tooltipTarget="farmDetailsPanel"
                  tooltipContentKey="description"
                />
              ))}
              <h5 className="ImportantSubText NoDetailsMessage">
                No measurements available
              </h5>
            </div>
          </div>
          {/* TODO: Fix so this still shows when no details available */}
          {/* Spacer */}
          <hr className="DisplayMobileOnly" />
          {/* Settings */}
          <Button
            colour="WhiteAlt"
            additionalClassNames="PrimaryButton NoBorder Wide DisplayMobileOnly"
            onClick={() => {
              dispatch(setOpenPanel("settings"));
            }}
            icon={<SettingsSVG height={20} width={20} className="BtnIcon" />}
            children="Settings"
          />
        </div>
      ) : (
        <h2>No farm details available</h2>
      )}
    </Panel>
  );
};

export default PanelFarmDetails;
